import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDepressionMode } from '../contexts/DepressionModeContext';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import LoadingSpinner from '../components/LoadingSpinner';
// Phase 2-A: 一時非表示（APP_IMPROVEMENT_PLAN.md参照）
// import WeeklyChanges from '../components/WeeklyChanges';
// import MoodRecordButtons from '../components/MoodRecordButtons';
// import MoodBadges from '../components/MoodBadges';
import TsumugiMessage from '../components/TsumugiMessage';
import DepressionModeToggle from '../components/DepressionModeToggle';
import AnnouncementBar from '../components/AnnouncementBar';
import ProductCarousel from '../components/affiliate/ProductCarousel';
import AdSenseWidget from '../components/affiliate/AdSenseWidget';
import api from '../api/api';
import '../styles/pages/MyPage.css';

/**
 * マイページ（V2 - 継続の価値 × うつ症状配慮）
 * MYPAGE_REDESIGN_V2.md に基づいて実装
 */
const MyPage = () => {
  const { user, plan, logout, loading: authLoading } = useAuth();
  const { depressionMode, ultraDepressionMode } = useDepressionMode();
  const navigate = useNavigate();

  const [dataLoading, setDataLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [usage, setUsage] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [showUsageDetails, setShowUsageDetails] = useState(false);
  const [widgetConfig, setWidgetConfig] = useState(null);

  // 新規追加: 現在の気分
  const [currentMood, setCurrentMood] = useState(null);

  // Phase 2-A: 一時非表示のため不要
  // const [weeklyRefreshTrigger, setWeeklyRefreshTrigger] = useState(0);

  // 初回訪問判定
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);

  // ページタイトル設定
  useEffect(() => {
    document.title = 'マイページ | はしもと栄養パートナー';
  }, []);

  // 初回訪問かチェック
  useEffect(() => {
    const hasVisitedMyPage = localStorage.getItem('hasVisitedMyPage');
    if (!hasVisitedMyPage) {
      setShowWelcomeMessage(true);
      localStorage.setItem('hasVisitedMyPage', 'true');
    }
  }, []);

  // Phase 2-A: 一時非表示のため不要
  // const handleMoodRecorded = async (mood) => {
  //   setWeeklyRefreshTrigger(prev => prev + 1);
  //   setCurrentMood(mood);
  // };

  // 今日の気分を取得
  useEffect(() => {
    // Wait for user to be loaded before fetching
    if (!user) return;

    const fetchTodayMood = async () => {
      try {
        const response = await api.get('/api/mood/today');
        if (response.data.success && response.data.mood) {
          setCurrentMood(response.data.mood.mood);
        }
      } catch (error) {
        // 404 = 今日の記録なし
      }
    };
    fetchTodayMood();
  }, [user]);

  // Widget設定読み込み（ログイン不要）
  useEffect(() => {
    const loadWidgetConfig = async () => {
      try {
        const response = await api.get('/api/affiliate/widget-config');
        if (response.data.success) {
          setWidgetConfig(response.data.config);
        }
      } catch (error) {
        console.error('Widget設定取得エラー:', error);
      }
    };
    loadWidgetConfig();
  }, []);

  // ユーザーデータ読み込み
  useEffect(() => {
    // Wait for user to be loaded before fetching
    if (!user) return;

    loadUserData();
  }, [user]);

  const loadUserData = async () => {
    try {
      setDataLoading(true);

      // プロフィール取得
      console.log('📊 Fetching profile...');
      const profileResponse = await api.get('/api/profile');
      console.log('✅ Profile response:', profileResponse.data);
      if (profileResponse.data.success) {
        setUserProfile(profileResponse.data.user);
      }

      // 利用状況取得 - 全5種類の利用状況を取得
      console.log('📊 Fetching usage...');
      const usageResponse = await api.get('/api/user/usage');
      console.log('✅ Usage response:', usageResponse.data);
      console.log('📊 Usage keys:', Object.keys(usageResponse.data || {}));
      if (usageResponse.data) {
        // バックエンドのレスポンス形式: { consultations, recipes, ocrGeneral, ocrBloodTest, tsumugiDaily }
        setUsage(usageResponse.data);
        console.log('✅ Usage state set');
      }

      // 相談履歴取得
      console.log('📊 Fetching consultations...');
      const consultationsResponse = await api.get('/api/consultations/history');
      console.log('✅ Consultations response:', consultationsResponse.data);
      if (consultationsResponse.data.success) {
        setConsultations(consultationsResponse.data.consultations || []);
      }

      // 保存レシピ取得
      console.log('📊 Fetching recipes...');
      const recipesResponse = await api.get('/api/recipes');
      console.log('✅ Recipes response:', recipesResponse.data);
      if (recipesResponse.data.success) {
        setSavedRecipes(recipesResponse.data.recipes || []);
      }
    } catch (error) {
      console.error('❌ データ読み込みエラー:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
    } finally {
      setDataLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };


  // プラン名表示
  const getPlanName = () => {
    switch (plan) {
      case 'free': return '無料プラン';
      case 'standard': return 'スタンダードプラン';
      case 'premium': return 'プレミアムプラン';
      case 'premium_plus': return 'プレミアムプラスプラン';
      case 'ultimate': return 'アルティメットプラン';
      default: return '未設定';
    }
  };

  // 利用状況バッジの計算
  const getUsageBadge = () => {
    console.log('🔍 getUsageBadge called, usage:', usage);
    if (!usage) {
      console.log('❌ usage is null/undefined');
      return null;
    }

    // 使用済みと上限を計算
    let totalUsed = 0;
    let totalLimit = 0;

    const features = ['consultations', 'recipes', 'ocrGeneral', 'ocrBloodTest'];
    features.forEach(feature => {
      console.log(`📊 Feature ${feature}:`, usage[feature]);
      if (usage[feature]) {
        const current = usage[feature].current || 0;
        const remaining = usage[feature].remaining || 0;
        const limit = usage[feature].limit || (current + remaining);

        if (limit > 0) {
          totalUsed += current;
          totalLimit += limit;
        }
      }
    });

    const totalRemaining = totalLimit - totalUsed;
    console.log(`📊 Total: limit=${totalLimit}, used=${totalUsed}, remaining=${totalRemaining}`);

    if (totalLimit === 0) {
      console.log('❌ totalLimit is 0, returning null');
      return null;
    }

    // 残り率で判定（残りが多いほど快適）
    const remainingPercentage = (totalRemaining / totalLimit) * 100;

    if (remainingPercentage >= 70) {
      return {
        icon: '✨',
        text: '快適',
        color: 'var(--success)',
        bgColor: 'rgba(76, 175, 80, 0.1)',
        description: 'たっぷり使えます'
      };
    } else if (remainingPercentage >= 40) {
      return {
        icon: '😊',
        text: '余裕',
        color: 'var(--info)',
        bgColor: 'rgba(33, 150, 243, 0.1)',
        description: 'まだ使えます'
      };
    } else if (remainingPercentage >= 20) {
      return {
        icon: '📊',
        text: '注意',
        color: 'var(--warning)',
        bgColor: 'rgba(255, 152, 0, 0.1)',
        description: '残り少なくなってきました'
      };
    } else {
      return {
        icon: '⚠️',
        text: '上限近',
        color: 'var(--error)',
        bgColor: 'rgba(244, 67, 54, 0.1)',
        description: '今月の上限に近づいています'
      };
    }
  };

  // 次回更新日のフォーマット
  const formatNextResetDate = () => {
    // 有料プランでcurrentPeriodEndがある場合
    if (user?.currentPeriodEnd && plan !== 'free') {
      const date = new Date(user.currentPeriodEnd);
      return `${date.getMonth() + 1}月${date.getDate()}日`;
    }
    // 無料プランは翌月1日
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return `${nextMonth.getMonth() + 1}月${nextMonth.getDate()}日`;
  };

  // プラン別利用目安表示
  const renderUsageDetails = () => {
    const planInfo = {
      free: { name: 'Freeプラン', monthlyPoints: 50 },
      standard: { name: 'Standardプラン', monthlyPoints: 900 },
      premium: { name: 'Premiumプラン', monthlyPoints: 1800 },
      premium_plus: { name: 'Premium Plusプラン', monthlyPoints: 3600 },
      ultimate: { name: 'Ultimateプラン', monthlyPoints: 5500 }
    };

    const currentPlan = planInfo[plan] || planInfo.free;

    // ポイント使用量を取得（キャンペーン中はAPIの値を優先）
    const pointsSpent = usage?.pointsSpent || 0;
    const monthlyPoints = usage?.monthlyPoints || currentPlan.monthlyPoints;
    const isCampaign = usage?.isCampaign || false;

    return (
      <div className="my-page__usage-details-content">
        <div className="my-page__usage-plan-info">
          <div className="my-page__usage-plan-header">
            <div className="my-page__usage-plan-label">
              あなたのプラン
            </div>
            <div className="my-page__usage-plan-name">
              {currentPlan.name}
              {isCampaign && (
                <span className="my-page__campaign-badge">キャンペーン中</span>
              )}
            </div>
          </div>

          <div className="my-page__usage-points-container">
            <div className="my-page__usage-plan-label">
              今月のご利用
            </div>
            <div className="my-page__usage-points-value">
              {pointsSpent}pt <span className="my-page__usage-points-limit">/ {monthlyPoints}pt</span>
            </div>
            <div className="my-page__usage-reset-date">
              次回更新: {formatNextResetDate()}
            </div>
          </div>
        </div>

        <button
          onClick={() => navigate('/usage-policy')}
          className="my-page__usage-details-btn"
        >
          詳しく見る →
        </button>
      </div>
    );
  };

  if (authLoading || dataLoading) {
    return (
      <AppLayout>
        <PageTitle
          title="マイページ"
          mascot={<img src="/tsumugi.png" alt="つむぎ" className="my-page__mascot-img" />}
        />
        <LoadingSpinner message="データを読み込んでいます..." size="medium" />
      </AppLayout>
    );
  }

  const displayName = userProfile?.profile?.displayName || user?.name || 'ゲスト';

  // プロフィール完成度を計算（表示されている項目のみカウント）
  const calculateProfileCompletion = () => {
    if (!userProfile || !userProfile.profile) return 0;

    const profile = userProfile.profile;
    let completed = 0;
    let total = 7; // 重要な項目のみカウント

    // 基本情報（5項目）
    if (profile.displayName) completed++; // 1. お名前
    if (profile.gender) completed++; // 2. 性別
    if (profile.age) completed++; // 3. 年齢
    if (profile.height) completed++; // 4. 身長
    if (profile.weight) completed++; // 5. 体重

    // 健康情報（2項目）
    if (profile.allergies && profile.allergies.length > 0) completed++; // 6. アレルギー
    if (profile.avoidFoods && profile.avoidFoods.length > 0) completed++; // 7. 避けたい食べ物

    console.log('📊 プロフィール完成度:', {
      completed,
      total,
      percentage: Math.round((completed / total) * 100),
      profile: {
        displayName: !!profile.displayName,
        gender: !!profile.gender,
        age: !!profile.age,
        height: !!profile.height,
        weight: !!profile.weight,
        allergies: profile.allergies?.length > 0,
        avoidFoods: profile.avoidFoods?.length > 0
      }
    });

    return Math.round((completed / total) * 100);
  };

  const profileCompletion = calculateProfileCompletion();

  // V2のプリセット選択肢に基づいてタイトルとアイコンを生成
  const generateConsultationTitle = (consultation) => {
    // 継続相談
    if (consultation.isFollowUp) {
      return { icon: '🔄', title: '前回相談の継続・追加質問', badge: '継続相談' };
    }

    // V2のpresetTypeが保存されている場合はそれを使用（優先）
    const presetType = consultation.presetType;
    if (presetType) {
      const presetMapping = {
        'morning_fatigue': { icon: '😴', title: '朝起きられない・一日中だるい', badge: '最も多い悩み' },
        'appetite_loss': { icon: '🍽️', title: '食欲がない・食べるのが億劫', badge: '人気' },
        'mental_health': { icon: '😔', title: '気分が優れない・元気が出ない', badge: 'メンタルケア' },
        'simple_easy': { icon: '🤷‍♀️', title: '何を食べたらいいか分からない', badge: '初めての方に' },
        'other': { icon: '💭', title: 'その他（自由に質問）', badge: null }
      };

      if (presetMapping[presetType]) {
        return presetMapping[presetType];
      }
    }

    // 後方互換性：presetTypeがない古いデータはテキスト解析（フォールバック）
    const consultationText = consultation.userInput?.consultation || '';
    const lowerText = consultationText.toLowerCase();

    // 朝起きられない・一日中だるい
    if (lowerText.includes('朝起きられない') || lowerText.includes('朝起きる') ||
        (lowerText.includes('一日中') && lowerText.includes('だるい')) ||
        (lowerText.includes('体が重') && lowerText.includes('だるい'))) {
      return { icon: '😴', title: '朝起きられない・一日中だるい', badge: '最も多い悩み' };
    }

    // 食欲がない・食べるのが億劫
    if (lowerText.includes('食欲がな') || lowerText.includes('食べるのが億劫') ||
        (lowerText.includes('食事') && lowerText.includes('億劫'))) {
      return { icon: '🍽️', title: '食欲がない・食べるのが億劫', badge: '人気' };
    }

    // 気分が優れない・元気が出ない
    if (lowerText.includes('気分が優れな') || lowerText.includes('元気が出な') ||
        lowerText.includes('気分が楽になる')) {
      return { icon: '😔', title: '気分が優れない・元気が出ない', badge: 'メンタルケア' };
    }

    // 何を食べたらいいか分からない
    if (lowerText.includes('何を食べたらいい') || lowerText.includes('何をどう食べれば') ||
        (lowerText.includes('バランスの良い食事') && lowerText.includes('具体的'))) {
      return { icon: '🤷‍♀️', title: '何を食べたらいいか分からない', badge: '初めての方に' };
    }

    // その他（自由に質問したい）
    return { icon: '💭', title: 'その他（自由に質問）', badge: null };
  };

  return (
    <AppLayout>
      <PageTitle
        title={`${displayName}さんのマイページ`}
        subtitle={getPlanName()}
        mascot={<img src="/tsumugi.png" alt="つむぎ" className="my-page__mascot-img" />}
      />

      <div className="tsumugi-container py-6" data-mode={ultraDepressionMode ? 'ultra-depression' : depressionMode ? 'depression' : 'normal'}>
          {/* お知らせバー（通常モード時のみ表示） */}
          {!depressionMode && <AnnouncementBar />}

          {/* 超おやすみモード時の最小限UI */}
          {ultraDepressionMode && (
            <>
              {/* つむぎからの優しいメッセージ */}
              <TsumugiMessage
                mood={currentMood}
                depressionMode={true}
                userName={displayName}
              />

              <div className="my-page__ultra-mode-minimal">
                <div className="tsumugi-card">
                  <p className="my-page__ultra-mode-message">
                    💜 辛い時は、何もしなくていいよ。<br />
                    話したくなったら、つむぎがここにいるよ。
                  </p>
                  <button
                    className="tsumugi-btn tsumugi-btn-primary w-full my-page__main-action"
                    onClick={() => navigate('/form-v2')}
                  >
                    💬 つむぎに話す
                  </button>
                </div>

                {/* モード切替 */}
                <DepressionModeToggle />
              </div>
            </>
          )}

          {/* おやすみモード時の簡略化UI */}
          {depressionMode && !ultraDepressionMode && (
            <>
              {/* つむぎからの優しいメッセージ */}
              <TsumugiMessage
                mood={currentMood}
                depressionMode={true}
                userName={displayName}
              />

              <div className="my-page__deep-mode-simple">
                <div className="tsumugi-card">
                  <p className="my-page__deep-mode-message">
                    今日は無理しなくていいよ。<br />
                    できることだけで大丈夫。
                  </p>
                  <button
                    className="tsumugi-btn tsumugi-btn-primary w-full my-page__main-action"
                    onClick={() => navigate('/form-v2')}
                  >
                    🌙 つむぎに話す
                  </button>
                  <button
                    className="tsumugi-btn tsumugi-btn-outline w-full my-page__secondary-action"
                    onClick={() => navigate('/recipe-hub')}
                  >
                    レシピを見る
                  </button>
                  <button
                    className="tsumugi-btn tsumugi-btn-outline w-full my-page__secondary-action"
                    onClick={() => navigate('/recipes')}
                  >
                    保存したレシピを見る
                  </button>
                </div>

                {/* おやすみモード切替 */}
                <DepressionModeToggle />
              </div>
            </>
          )}

          {/* 通常表示 */}
          {!depressionMode && (
            <>
              {/* つむぎからの寄り添いメッセージ */}
              <TsumugiMessage
                mood={currentMood}
                depressionMode={false}
                userName={displayName}
              />

              {/* 初回訪問時のウェルカムメッセージ */}
              {showWelcomeMessage && (
                <div className="tsumugi-card tsumugi-card--mint my-page__welcome-card">
                  <div className="my-page__welcome-content">
                    <div className="my-page__welcome-icon">
                      <img src="/tsumugi.png" alt="つむぎ" className="my-page__welcome-img" />
                    </div>
                    <h3 className="my-page__welcome-title">
                      ようこそ、{displayName}さん！
                    </h3>
                    <p className="my-page__welcome-text">
                      はしもと栄養パートナーへようこそ。<br />
                      私はあなたの栄養と健康をサポートする「つむぎ」です。
                    </p>
                    <p className="my-page__welcome-text">
                      あなたのペースで、無理なく、一緒に歩んでいきましょう。
                    </p>
                    <button
                      className="tsumugi-btn tsumugi-btn-primary my-page__welcome-btn"
                      onClick={() => setShowWelcomeMessage(false)}
                    >
                      わかりました
                    </button>
                  </div>
                </div>
              )}

              {/*
               * Phase 2-A: 以下の機能を一時非表示
               * - 気分記録ボタン: 毎日の記録は継続が難しい
               * - WeeklyChanges: 利用率低い
               * - MoodBadges: コア価値（疲労管理）と無関係、ゲーミフィケーションは逆効果の可能性
               *
               * データは保持（将来の復活可能性）
               * APP_IMPROVEMENT_PLAN.md 参照
               */}
              {/*
              <MoodRecordButtons
                onRecorded={handleMoodRecorded}
                depressionMode={depressionMode}
              />
              <WeeklyChanges key={weeklyRefreshTrigger} />
              {!depressionMode && <MoodBadges />}
              */}

              {/* Google AdSense広告 - Premium以上には非表示 */}
              {widgetConfig && !['premium', 'premium_plus', 'ultimate'].includes(plan) && (
                <AdSenseWidget config={widgetConfig.adsense} location="my_page" />
              )}

              {/* アカウント情報カード */}
              <div className="tsumugi-card">
          <h3 className="text-base font-bold mb-4 my-page__section-title flex items-center gap-2">
            <img src="/mypage.png" alt="" className="w-6 h-6" />
            アカウント情報
          </h3>

          {/* プロフィール完成度（Depression Modeでは非表示） */}
          {!depressionMode && (
            <div className="my-page__profile-completion">
              <div className="my-page__completion-header">
                <span className="my-page__completion-label">プロフィール完成度</span>
                <span className="my-page__completion-value">{profileCompletion}%</span>
              </div>
              <div className="my-page__completion-bar-bg">
                <div
                  className="my-page__completion-bar-fill"
                  style={{ width: `${profileCompletion}%` }}
                ></div>
              </div>
              {profileCompletion < 100 && (
                <p className="my-page__completion-hint">
                  もっと知らせてくれると嬉しいな（任意だよ）
                </p>
              )}
            </div>
          )}

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="my-page__info-label">お名前</span>
              <span className="my-page__info-value">{displayName}</span>
            </div>

            <div className="flex justify-between">
              <span className="my-page__info-label">メールアドレス</span>
              <span className="my-page__info-value">{user?.email}</span>
            </div>

            <div className="flex justify-between">
              <span className="my-page__info-label">プラン</span>
              <span className="tsumugi-tag tsumugi-tag-peach">{getPlanName()}</span>
            </div>

          </div>

          {/* 利用状況 - バッジ + 折りたたみ詳細 */}
          <div className="mt-4">
            <h4 className="my-page__usage-section-title">今月の健康サポート</h4>
            {(() => {
              const badge = getUsageBadge();
              if (!badge) return <p className="text-hint text-xs">読み込み中...</p>;

              return (
                <div>
                  <div className="my-page__usage-badge" style={{
                    background: badge.bgColor,
                    border: `1px solid ${badge.color}`
                  }}>
                    <div className="my-page__usage-badge-content">
                      <span className="my-page__usage-badge-icon">{badge.icon}</span>
                      <div className="my-page__usage-badge-info">
                        <div className="my-page__usage-badge-text" style={{ color: badge.color }}>
                          {badge.text}
                        </div>
                        <div className="my-page__usage-badge-description">
                          {badge.description}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowUsageDetails(!showUsageDetails)}
                      className="my-page__usage-toggle-btn"
                      style={{ color: badge.color }}
                    >
                      {showUsageDetails ? '閉じる ▲' : '詳細 ▼'}
                    </button>
                  </div>

                  {showUsageDetails && (
                    <div className="my-page__usage-details-container">
                      <div className="my-page__usage-details-title">
                        今月の利用状況
                      </div>
                      {renderUsageDetails()}
                      <div className="my-page__usage-reset-notice">
                        来月1日に利用回数が更新されます
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* 区切り線 + おやすみモード切替 */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <DepressionModeToggle />
          </div>
        </div>

        {/* 保存レシピプレビュー */}
        <div className="tsumugi-card">
          <h3 className="tsumugi-card-title">🍽️ 保存したレシピ</h3>

          {savedRecipes.length === 0 ? (
            <div className="consultation-empty">
              <div className="consultation-empty__icon">
                <img src="/recipe.png" alt="" className="w-24 h-24" />
              </div>
              <p className="consultation-empty__title">まだレシピを保存していません</p>
              <p className="consultation-empty__text">
                つむぎとの会話や相談の中で、<br />
                気に入ったレシピを保存できます
              </p>
            </div>
          ) : (
            <>
              <div className="consultation-list">
                {savedRecipes.slice(0, 3).map((recipe) => (
                  <div
                    key={recipe._id}
                    className="consultation-item"
                    onClick={() => navigate(`/recipes/${recipe._id}`)}
                  >
                    <div className="consultation-item__header">
                      <div className="my-page__consultation-header">
                        <span className="my-page__consultation-icon">📖</span>
                        <span className="my-page__consultation-title">
                          {recipe.title}
                        </span>
                        {!depressionMode && recipe.isFavorite && (
                          <span className="tsumugi-tag tsumugi-tag-peach my-page__consultation-badge">
                            ピン留め
                          </span>
                        )}
                      </div>
                      <span className="consultation-item__date">
                        {new Date(recipe.savedAt || recipe.createdAt).toLocaleDateString('ja-JP', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {savedRecipes.length > 3 && (
                <button
                  className="tsumugi-btn tsumugi-btn-outline w-full my-page__consultation-item-container"
                  onClick={() => navigate('/recipes')}
                >
                  すべてのレシピを見る ({savedRecipes.length}件)
                </button>
              )}
            </>
          )}
        </div>

        {/* 相談履歴カード */}
        <div className="tsumugi-card">
          <h3 className="tsumugi-card-title">📋 最近の相談</h3>

          {consultations.length === 0 ? (
            <div className="consultation-empty">
              <div className="consultation-empty__icon">
                <img src="/chat.png" alt="" className="w-24 h-24" />
              </div>
              <p className="consultation-empty__title">まだ相談履歴がありません</p>
              <p className="consultation-empty__text">
                焦らなくて大丈夫。<br />
                気が向いた時に、いつでも相談してね
              </p>
              <button
                className="tsumugi-btn tsumugi-btn-primary my-page__consultation-item-container"
                onClick={() => navigate('/form-v2')}
              >
                初めての相談をする
              </button>
            </div>
          ) : (
            <div className="consultation-list">
              {consultations.slice(0, 3).map((consultation) => {
                const titleData = generateConsultationTitle(consultation);
                const consultationText = consultation.userInput?.consultation || '';
                const displayText = consultationText.length > 80
                  ? consultationText.substring(0, 80) + '...'
                  : consultationText;

                return (
                  <div
                    key={consultation._id}
                    className="consultation-item"
                    onClick={() => navigate(`/consultation/${consultation._id}`)}
                  >
                    <div className="consultation-item__header">
                      <div className="my-page__consultation-header">
                        <span className="my-page__consultation-icon">{titleData.icon}</span>
                        <span className="my-page__consultation-title">
                          {titleData.title}
                        </span>
                        {!depressionMode && titleData.badge && (
                          <span className="tsumugi-tag tsumugi-tag-lilac my-page__consultation-badge">
                            {titleData.badge}
                          </span>
                        )}
                      </div>
                      <span className="consultation-item__date">
                        {new Date(consultation.createdAt).toLocaleDateString('ja-JP', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    </div>
                    {consultationText && consultationText.length > 20 && (
                      <p className="consultation-item__question">
                        {displayText}
                      </p>
                    )}
                  </div>
                );
              })}

              {consultations.length > 3 && (
                <button
                  className="tsumugi-btn tsumugi-btn-outline w-full my-page__consultation-item-container"
                  onClick={() => navigate('/consultation-history')}
                >
                  すべての履歴を見る ({consultations.length}件)
                </button>
              )}
            </div>
          )}
        </div>

        {/* アフィリエイト商品カルーセル: マイページでのおすすめ商品 */}
        <ProductCarousel location="mypage" />

              {/* 設定・その他 */}
              <div className="tsumugi-card">
                <h3 className="tsumugi-card-title">⚙️ 設定・その他</h3>
                <div className="space-y-3">
                  <button
                    className="tsumugi-btn tsumugi-btn-outline w-full"
                    onClick={() => navigate('/profile/edit')}
                  >
                    プロフィール編集
                  </button>
                  <button
                    className="tsumugi-btn tsumugi-btn-outline w-full"
                    onClick={() => navigate('/plans')}
                  >
                    プラン変更
                  </button>
                  <button
                    className="tsumugi-btn tsumugi-btn-outline w-full"
                    onClick={handleLogout}
                  >
                    ログアウト
                  </button>
                </div>
              </div>
            </>
          )}
      </div>
    </AppLayout>
  );
};

export default MyPage;
